import { GlowFilter } from '@pixi/filter-glow';
import gsap from 'gsap';
import { Howl } from 'howler';
// Use pixi.js-legacy which provides automatic WebGL/Canvas renderer fallback
import * as PIXI from 'pixi.js-legacy';
import { assetPath, assets } from '@/lib/assets';
import { playSound } from '@/lib/utils/playSound';

// --------------------- Types & Interfaces ------------------------

/**
 * Game state types
 */
type GameStatus = 'idle' | 'playing' | 'gameover';

/**
 * Light definition interface
 */
interface Light {
  name: string;
  color: number;
  x: number;
  y: number;
  size: number;
  texture?: PIXI.Texture;
  brokenTex?: PIXI.Texture;
  sound: string;
}

/**
 * Sound definition interface
 */
interface SoundAsset {
  src: string;
  preload: boolean;
  player?: Howl;
}

/**
 * Callback functions interface
 */
interface GameCallbacks {
  onGameStarted: () => void;
  onBrokenLedDisplayed: (continueCallback: () => void) => void;
  onScoreUpdated: (score: number) => void;
  onGameOver: (score: number) => void;
  onStatusChanged: (status: GameStatus) => void;
}

/**
 * Public API interface
 */
interface GameAPI {
  initApp: (wrapper: HTMLCanvasElement) => Promise<void>;
  updateCallbacks: (callbacks: GameCallbacks) => void;
  destroyApp: () => void;
  startGame: () => void;
  endGame: () => void;
  getStatus: () => GameStatus;
}

// --------------------- Constants ------------------------

// Game dimensions
const APP_WIDTH = 540;
const APP_HEIGHT = 960;

// Asset paths
const gameAssetPath = `${assetPath}game_lights`;
const imagePath = `${gameAssetPath}/images`;
const soundPath = `${gameAssetPath}/sounds`;

// Game elements
const lights: Light[] = [
  {
    name: 'red',
    color: 0xff0000,
    x: 289,
    y: 488,
    size: 50,
    sound: '01.mp3',
  },
  {
    name: 'green',
    color: 0x00ee33,
    x: 268,
    y: 600,
    size: 20,
    sound: '02.mp3',
  },
  {
    name: 'yellow',
    color: 0xfedd1c,
    x: 435,
    y: 558,
    size: 60,
    sound: '03.mp3',
  },
  {
    name: 'orange',
    color: 0xff740b,
    x: 151,
    y: 550,
    size: 70,
    sound: '04.mp3',
  },
  {
    name: 'purple',
    color: 0xb022cd,
    x: 412,
    y: 700,
    size: 80,
    sound: '05.mp3',
  },
  {
    name: 'blue',
    color: 0x3da1f7,
    x: 101,
    y: 631,
    size: 70,
    sound: '06.mp3',
  },
];

// Game sounds
const sounds: Record<string, SoundAsset> = {
  fail: { src: assets.game_lights.sounds.fail, preload: true },
  click: { src: assets.game_lights.sounds.click, preload: true },
  success: { src: assets.game_lights.sounds.success, preload: true },
  broken: { src: assets.game_lights.sounds.broken, preload: true },
};

// --------------------- State & Variables ------------------------

// Game engine
let pixiApp: PIXI.Application;

// Game state
let score = 0;
let sequence: number[] = [];
let playerSequence: number[] = [];
let status: GameStatus = 'idle';
let buttons: PIXI.Container[] = [];
let brokenSequenceDisplayed = false;
let brokenLedIndex = Math.floor(Math.random() * lights.length);

// Callback functions
let onGameStarted: (() => void) | null = null;
let onScoreUpdated: ((score: number) => void) | null = null;
let onGameOver: ((score: number) => void) | null = null;
let onStatusChanged: ((status: GameStatus) => void) | null = null;
let onBrokenLedDisplayed: ((continueCallback: () => void) => void) | null =
  null;

// --------------------- Initialization ------------------------

/**
 * Initialize the game application
 * @param wrapper Canvas element to render into
 */
const initApp = async (wrapper: HTMLCanvasElement): Promise<void> => {
  try {
    console.log('Initializing LightsGame...');

    // Step 1: Create PIXI application with renderer detection
    await initRenderer(wrapper);

    // Step 2: Preload sounds but don't start them yet
    await preloadSounds();

    // Step 3: Load textures
    await loadTextures();

    // Step 4: Create game buttons
    createButtons();

    // Step 5: Add user interaction handler to unlock audio
    setupAudioUnlock();

    console.log('LightsGame initialization complete');
  } catch (error) {
    console.error('Failed to initialize game:', error);
    handleInitializationFailure();
  }
};

/**
 * Initialize the PIXI renderer
 * @param wrapper Canvas element to render into
 */
const initRenderer = async (wrapper: HTMLCanvasElement): Promise<void> => {
  try {
    // Let pixi.js-legacy handle the renderer detection and fallback automatically
    pixiApp = new PIXI.Application({
      width: APP_WIDTH,
      height: APP_HEIGHT,
      backgroundAlpha: 0,
      view: wrapper,
    });

    console.log('Renderer created:', pixiApp.renderer.type);

    // Set up game update loop
    pixiApp.ticker.add(onGameUpdate);
  } catch (error) {
    console.error('Failed to initialize PIXI renderer:', error);
    throw new Error('Could not initialize renderer');
  }
};

/**
 * Handle complete initialization failure
 */
const handleInitializationFailure = (): void => {
  status = 'gameover';
  if (onStatusChanged) onStatusChanged(status);
  if (onGameOver) onGameOver(0);
};

/**
 * Update callback references
 * @param callbacks Object containing all callback functions
 */
const updateCallbacks = (callbacks: GameCallbacks): void => {
  onGameStarted = callbacks.onGameStarted;
  onScoreUpdated = callbacks.onScoreUpdated;
  onGameOver = callbacks.onGameOver;
  onStatusChanged = callbacks.onStatusChanged;
  onBrokenLedDisplayed = callbacks.onBrokenLedDisplayed;
};

// --------------------- Asset Loading ------------------------

/**
 * Preload all game sounds
 */
const preloadSounds = async (): Promise<void> => {
  try {
    let soundsLoaded = 0;
    const totalSounds = Object.keys(sounds).length;

    // Create promises for sound loading
    const loadPromises = Object.values(sounds).map((sound) => {
      return new Promise<void>((resolve) => {
        sound.player = new Howl({
          src: [sound.src],
          preload: sound.preload,
        });

        sound.player.on('loaderror', (error: any) => {
          console.error('Failed to load sound', sound.src, error);
          resolve(); // Resolve even on error to continue
        });

        sound.player.on('load', () => {
          soundsLoaded += 1;
          if (soundsLoaded === totalSounds) {
            console.log('All sounds loaded');
          }
          resolve();
        });
      });
    });

    // Wait for all sounds to load (or fail)
    await Promise.all(loadPromises);
  } catch (error) {
    console.error('Error during sound preloading:', error);
    // Continue execution even if sound loading fails
  }
};

/**
 * Load all light textures
 */
const loadTextures = async (): Promise<void> => {
  try {
    for (const light of lights) {
      try {
        console.log(`Loading texture for ${light.name}`);

        // Try loading WebP first
        try {
          // Load normal texture
          light.texture = await PIXI.Assets.load(
            `${imagePath}/light_${light.name}.webp`
          );
        } catch (webpError) {
          console.warn(
            `WebP failed for ${light.name}, falling back to PNG`,
            webpError
          );
          // Fallback to PNG
          light.texture = await PIXI.Assets.load(
            `${imagePath}/light_${light.name}.png`
          );
        }

        // Load broken textures for all lights so we can change the broken LED later
        try {
          light.brokenTex = await PIXI.Assets.load(
            `${imagePath}/light_${light.name}_broken.webp`
          );
        } catch (webpError) {
          console.warn(
            `WebP broken texture failed for ${light.name}, falling back to PNG`,
            webpError
          );
          light.brokenTex = await PIXI.Assets.load(
            `${imagePath}/light_${light.name}_broken.png`
          );
        }
      } catch (error) {
        console.error(`Failed to load texture for ${light.name}:`, error);
        // Create a fallback texture
        light.texture = createFallbackTexture(light.color);
        light.brokenTex = light.texture; // Use same fallback for broken texture
      }
    }
  } catch (error) {
    console.error('Error during texture loading:', error);
    throw new Error('Failed to load essential game textures');
  }
};

/**
 * Create a fallback texture when loading fails
 * @param color The color to use for the fallback texture
 */
const createFallbackTexture = (color: number): PIXI.Texture => {
  try {
    // If no app, use empty texture
    if (!pixiApp) {
      return PIXI.Texture.EMPTY;
    }

    // Create a basic colored circle as fallback
    const graphics = new PIXI.Graphics();

    // Use modern Color API instead of deprecated hex2rgb
    // Convert hex color to Color object
    const colorObj = new PIXI.Color(color);

    graphics.beginFill(colorObj.toNumber());
    graphics.drawCircle(100, 100, 100);
    graphics.endFill();

    // Using non-null assertion operator since we've checked pixiApp above
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return pixiApp!.renderer.generateTexture(graphics);
  } catch (error) {
    console.error('Failed to create fallback texture:', error);
    // Last resort: return empty texture
    return PIXI.Texture.EMPTY;
  }
};

// --------------------- Game Components ------------------------

/**
 * Create game buttons from loaded textures
 */
const createButtons = (): void => {
  if (!pixiApp) {
    console.error('Cannot create buttons: PIXI app not initialized');
    return;
  }

  buttons = lights.map((light, index) => {
    try {
      const texture = light.texture as PIXI.Texture;
      const button = new PIXI.Container();
      button.alpha = 0;

      // Create sprite from texture
      const buttonSprite = PIXI.Sprite.from(texture);
      buttonSprite.alpha = 0.75;

      // Set size and position
      const textureScale = 0.33;
      buttonSprite.width = texture.width * textureScale;
      buttonSprite.height = texture.height * textureScale;
      buttonSprite.x = -buttonSprite.width / 2;
      buttonSprite.y = -buttonSprite.height / 2;

      // Add glow filters - they will only work in WebGL mode
      try {
        buttonSprite.filters = [
          new GlowFilter({
            distance: 15,
            outerStrength: 3,
            color: light.color,
          }),
        ];
      } catch (error) {
        console.warn(
          'GlowFilter not supported, continuing without filters:',
          error
        );
      }

      // Add sprite to button container
      button.addChild(buttonSprite);
      button.position.set(light.x, light.y);

      // Set up interactivity
      button.eventMode = 'none';
      button.cursor = 'pointer';
      button.on('pointerdown', () => {
        lightUpButton(index, true);
        handlePlayerInput(index);
      });

      pixiApp.stage.addChild(button);
      return button;
    } catch (error) {
      console.error(`Error creating button ${index}:`, error);
      // Return an empty container as fallback
      return new PIXI.Container();
    }
  });
};

// --------------------- Game Logic ------------------------

/**
 * Game update loop - called each frame
 */
const onGameUpdate = (): void => {
  // Update game state each frame if needed
  // Currently not used, but required for ticker
};

/**
 * Reset all buttons to initial state
 */
const resetButtons = (): void => {
  console.log('Resetting buttons');

  // Reset each button (except the broken LED if broken sequence has been displayed)
  buttons.forEach((button, index) => {
    const isBroken = index === brokenLedIndex && brokenSequenceDisplayed;

    // Skip resetting the broken LED if it's already broken
    if (!isBroken) {
      gsap.killTweensOf(button);
      button.alpha = 0.0;
      button.scale.set(1);
    }
  });
};

/**
 * Enable button interaction
 */
const enableButtons = (): void => {
  // Enable all buttons except the broken LED (if it's already broken)
  buttons.forEach((button, index) => {
    const isBroken = index === brokenLedIndex && brokenSequenceDisplayed;

    // Don't allow interaction with the broken LED
    button.eventMode = isBroken ? 'none' : 'dynamic';
  });
};

/**
 * Disable button interaction
 */
const disableButtons = (): void => {
  buttons.forEach((button) => {
    button.eventMode = 'none';
  });
};

/**
 * Start a new game
 */
const startGame = (): void => {
  // Reset game state
  sequence = [];
  playerSequence = [];
  score = 0;
  status = 'playing';
  brokenSequenceDisplayed = false;

  // Reset and randomize the broken LED
  resetBrokenLed();

  // Start game ticker
  if (pixiApp?.ticker) {
    pixiApp.ticker.start();
  }

  // Notify about game start
  if (onGameStarted) onGameStarted();
  if (onStatusChanged) onStatusChanged(status);

  // Start the sequence after a delay
  setTimeout(generateSequence, 1000);
};

/**
 * End the current game
 */
const endGame = (): void => {
  status = 'gameover';

  if (pixiApp?.ticker) {
    pixiApp.ticker.stop();
  }

  if (onGameOver) onGameOver(score);
  if (onStatusChanged) onStatusChanged(status);
};

/**
 * Clean up resources when game is destroyed
 */
const destroyApp = (): void => {
  // Stop any animations
  buttons.forEach((button) => {
    gsap.killTweensOf(button);
  });

  // Unload sounds after a short delay to let them finish playing
  setTimeout(() => {
    Object.values(sounds).forEach((sound) => {
      sound.player?.unload();
    });
  }, 1000);

  // Destroy PIXI application if it exists
  if (pixiApp) {
    pixiApp.destroy(true);
  }
};

/**
 * Get current game status
 */
const getStatus = (): GameStatus => status;

// --------------------- Gameplay Mechanics ------------------------

/**
 * Generate next sequence step
 */
const generateSequence = (): void => {
  // Check if we should display broken LED animation
  if (sequence.length === 3 && !brokenSequenceDisplayed) {
    displayBrokenLed();
    return;
  }

  // Get the last light in the sequence (if any)
  const lastLightIndex =
    sequence.length > 0 ? sequence[sequence.length - 1] : -1;

  // Try to generate a new light index that is:
  // 1. Not the broken LED
  // 2. Not the same as the last light in the sequence
  let attempts = 0;
  let newLedIndex: number;

  do {
    newLedIndex = Math.floor(Math.random() * lights.length);
    attempts++;

    // Prevent infinite loop in case of extremely unlikely scenario
    if (attempts > 20) {
      console.warn('Failed to find a different light after many attempts');
      break;
    }
  } while (
    newLedIndex === brokenLedIndex || // Avoid broken LED
    (lastLightIndex !== -1 && newLedIndex === lastLightIndex) // Avoid same as last light
  );

  // Add the new light to the sequence and play it
  sequence = [...sequence, newLedIndex];
  console.log(
    'Generated sequence:',
    sequence.map((idx) => lights[idx].name).join(', ')
  );
  playSequence(sequence);
};

/**
 * Handle player button input
 */
const handlePlayerInput = (index: number): void => {
  // Add this input to the player's sequence
  playerSequence = [...playerSequence, index];

  // Check if this input is correct (matches the same position in the sequence)
  const currentPosition = playerSequence.length - 1;

  // If this input matches the expected light at this position
  if (sequence[currentPosition] === index) {
    // Input is correct

    // If player has completed the full sequence
    if (playerSequence.length === sequence.length) {
      // Full sequence correct - advance to next level
      handleCorrectSequence();
    }
    // Otherwise continue accepting inputs
  } else {
    // Input is incorrect - end game immediately
    console.log('Incorrect input detected');
    handleIncorrectSequence();
  }
};

/**
 * Handle correct sequence completion
 */
const handleCorrectSequence = (): void => {
  // Disable buttons while processing
  disableButtons();

  // Increment score
  score += 1;
  playerSequence = [];
  if (onScoreUpdated) onScoreUpdated(score);

  // Play success sound
  setTimeout(() => {
    sounds.success.player?.play();
  }, 300);

  // Start next sequence after a delay
  setTimeout(() => {
    resetButtons();
    generateSequence();
  }, 1500);
};

/**
 * Handle incorrect sequence input
 */
const handleIncorrectSequence = (): void => {
  // Disable buttons
  disableButtons();

  // Play fail sound
  sounds.fail.player?.play();

  // End the game
  endGame();
};

/**
 * Play the sequence for the player to repeat
 */
const playSequence = (seq: number[]): void => {
  if (!pixiApp) return;

  // Calculate delay between lights
  // Original formula: Math.max(1000 - score * 100, 200)
  // New formula with slower speed reduction and higher minimum
  const baseDelay = 1000; // Starting delay (1 second)
  const minDelay = 400; // Minimum delay (400ms instead of 200ms)
  const speedReduction = 60; // Reduce by 60ms per level (instead of 100ms)

  const delayBetween = Math.max(baseDelay - score * speedReduction, minDelay);

  console.log(`Playing sequence at speed: ${delayBetween}ms delay`);

  let delay = 0;

  // Play each light in the sequence with a delay
  seq.forEach((index) => {
    setTimeout(() => {
      lightUpButton(index, delayBetween < 600);
    }, delay);
    delay += delayBetween;
  });

  // Enable buttons after the sequence finishes
  setTimeout(() => {
    enableButtons();
  }, delay);
};

/**
 * Display broken LED animation and prompt
 */
const displayBrokenLed = async (): Promise<void> => {
  if (!pixiApp) {
    console.error('PixiApp not initialized');
    return;
  }

  // Get the current broken button
  const brokenButton = buttons[brokenLedIndex];
  if (!brokenButton || brokenButton.children.length === 0) {
    console.error('Broken button not found or has no children');
    return;
  }

  const ledSprite = brokenButton.children[0] as PIXI.Sprite;

  // Make sure we have a broken texture for this LED
  if (!lights[brokenLedIndex].brokenTex) {
    console.error(
      'No broken texture found for LED:',
      lights[brokenLedIndex].name
    );
    return;
  }

  const brokenLedTexture = lights[brokenLedIndex].brokenTex as PIXI.Texture;
  const brokenLedSprite = PIXI.Sprite.from(brokenLedTexture);

  // Set up broken LED sprite properties
  brokenLedSprite.width = ledSprite.width;
  brokenLedSprite.height = ledSprite.height;
  brokenLedSprite.x = ledSprite.x;
  brokenLedSprite.y = ledSprite.y;
  brokenLedSprite.name = 'broken_led'; // Add name for easier identification
  brokenButton.addChild(brokenLedSprite);

  // Set initial states
  ledSprite.alpha = 0;
  brokenLedSprite.alpha = 0;
  brokenButton.alpha = 1;

  // Animate the broken LED sequence
  gsap
    .timeline()
    // Blink the normal LED several times
    .add(
      gsap
        .timeline()
        .to(ledSprite, {
          onStart: () => {
            sounds.click.player?.play();
          },
          pixi: {
            alpha: 1,
          },
          duration: 0.05,
        })
        .to(ledSprite, {
          pixi: {
            alpha: 0.0,
          },
          duration: 0.05,
        })
        .repeat(5)
    )
    // Show the broken LED with sound
    .to(brokenLedSprite, {
      onStart: () => {
        sounds.broken.player?.play();
      },
      pixi: {
        alpha: 1,
      },
      duration: 0.1,
    })
    // Notify player through callback
    .then(() => {
      // Stop the game ticker
      if (pixiApp?.ticker) {
        pixiApp.ticker.stop();
      }

      // Call the broken LED callback with a continue function
      setTimeout(() => {
        if (onBrokenLedDisplayed) {
          onBrokenLedDisplayed(() => {
            // Resume the game when player acknowledges
            setTimeout(() => {
              brokenSequenceDisplayed = true;
              if (pixiApp?.ticker) {
                pixiApp.ticker.start();
              }
              generateSequence();
            }, 1000);
          });
        }
      }, 1000);
    });
};

/**
 * Set up handler to unlock audio on first user interaction
 */
const setupAudioUnlock = (): void => {
  // Setup interaction handler for audio context unlocking
  const unlockAudio = () => {
    // Unlock Howler audio context if needed
    if (Howler.ctx?.state !== 'running') {
      Howler.ctx
        .resume()
        .then(() => {
          console.log('AudioContext resumed successfully');
        })
        .catch((error) => {
          console.error('Failed to resume AudioContext:', error);
        });
    }

    // Remove event listeners once audio is unlocked
    window.removeEventListener('click', unlockAudio);
    window.removeEventListener('touchstart', unlockAudio);
    window.removeEventListener('keydown', unlockAudio);
  };

  // Add event listeners for user interaction
  window.addEventListener('click', unlockAudio);
  window.addEventListener('touchstart', unlockAudio);
  window.addEventListener('keydown', unlockAudio);
};

/**
 * Light up a button with animation
 */
const lightUpButton = (index: number, fast = false): void => {
  if (!pixiApp || index >= buttons.length) return;

  const button = buttons[index];
  // Play the sound associated with this light
  playSound(`${soundPath}/notes/${lights[index].sound}`, {
    volume: 2,
  });

  // Animate the button
  gsap
    .timeline()
    .to(button, {
      pixi: {
        scale: 1.02,
        alpha: 1,
      },
      duration: 0.1,
    })
    .to(button, {
      pixi: {
        scale: 1,
        alpha: 0.0,
      },
      duration: 0.1,
      delay: fast ? 0.2 : 0.5,
    });
};

/**
 * Reset and randomize the broken LED
 */
const resetBrokenLed = (): void => {
  // Clear any existing broken LED visual state
  if (
    buttons.length > 0 &&
    brokenLedIndex >= 0 &&
    brokenLedIndex < buttons.length
  ) {
    const oldBrokenButton = buttons[brokenLedIndex];

    // Reset the button's visual state
    if (oldBrokenButton) {
      // Remove any additional sprites (like broken LED sprite)
      while (oldBrokenButton.children.length > 1) {
        oldBrokenButton.removeChildAt(oldBrokenButton.children.length - 1);
      }

      // Reset the main sprite's alpha
      if (oldBrokenButton.children.length > 0) {
        (oldBrokenButton.children[0] as PIXI.Sprite).alpha = 0.75;
      }

      // Reset button state
      oldBrokenButton.alpha = 0.0;
      oldBrokenButton.scale.set(1);
      oldBrokenButton.eventMode = 'dynamic';
    }
  }

  // Choose a new random broken LED
  brokenLedIndex = Math.floor(Math.random() * lights.length);
  console.log(`New broken LED: ${lights[brokenLedIndex].name}`);
};

// --------------------- Public API ------------------------

/**
 * Exported game API
 */
export const GameLights: GameAPI = {
  initApp,
  updateCallbacks,
  destroyApp,
  startGame,
  endGame,
  getStatus,
};
